import { createServerFn } from "@tanstack/react-start";

const GATEWAY_URL = "https://connector-gateway.lovable.dev/google_maps";

const ORIGIN = { latitude: 35.25766941170577, longitude: -112.1222817560173 }; // I-40 Exit 167
const DESTINATION = { latitude: 35.16952, longitude: -112.06318 }; // 6929 E Rosilda Springs Rd approx
const WAYPOINTS = [
  { latitude: 35.182292477027254, longitude: -112.03294158792905 },
  { latitude: 35.16535060453394, longitude: -112.039490305055 },
  { latitude: 35.16999946658483, longitude: -112.05388234900869 },
];

export type GettingHereRoute = {
  polyline: string | null;
  distanceMeters: number | null;
  durationSeconds: number | null;
  origin: { lat: number; lng: number };
  destination: { lat: number; lng: number };
  waypoints: { lat: number; lng: number }[];
};

export const getGettingHereRoute = createServerFn({ method: "GET" }).handler(
  async (): Promise<GettingHereRoute> => {
    const lovableKey = process.env.LOVABLE_API_KEY;
    const mapsKey = process.env.GOOGLE_MAPS_API_KEY ?? process.env.GOOGLE_MAPS_API_KEY_1;
    const baseShape: GettingHereRoute = {
      polyline: null,
      distanceMeters: null,
      durationSeconds: null,
      origin: { lat: ORIGIN.latitude, lng: ORIGIN.longitude },
      destination: { lat: DESTINATION.latitude, lng: DESTINATION.longitude },
      waypoints: WAYPOINTS.map((w) => ({ lat: w.latitude, lng: w.longitude })),
    };

    if (!lovableKey || !mapsKey) return baseShape;

    try {
      const res = await fetch(
        `${GATEWAY_URL}/routes/directions/v2:computeRoutes`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${lovableKey}`,
            "X-Connection-Api-Key": mapsKey,
            "Content-Type": "application/json",
            "X-Goog-FieldMask":
              "routes.polyline.encodedPolyline,routes.distanceMeters,routes.duration",
          },
          body: JSON.stringify({
            origin: { location: { latLng: ORIGIN } },
            destination: { location: { latLng: DESTINATION } },
            intermediates: WAYPOINTS.map((latLng) => ({
              location: { latLng },
              via: true,
            })),
            travelMode: "DRIVE",
            routingPreference: "TRAFFIC_UNAWARE",
            polylineQuality: "HIGH_QUALITY",
          }),
        },
      );

      if (!res.ok) {
        console.error("Routes API error", res.status, await res.text());
        return baseShape;
      }
      const data = (await res.json()) as {
        routes?: Array<{
          polyline?: { encodedPolyline?: string };
          distanceMeters?: number;
          duration?: string;
        }>;
      };
      const r = data.routes?.[0];
      if (!r?.polyline?.encodedPolyline) return baseShape;
      const durSec = r.duration ? parseInt(r.duration.replace("s", ""), 10) : null;
      return {
        ...baseShape,
        polyline: r.polyline.encodedPolyline,
        distanceMeters: r.distanceMeters ?? null,
        durationSeconds: Number.isFinite(durSec as number) ? (durSec as number) : null,
      };
    } catch (err) {
      console.error("getGettingHereRoute failed", err);
      return baseShape;
    }
  },
);
